import { GlobalContext } from "@/Context/GlobalProvider";
import PropertyCardMini from "@/Pages/Properties/Items/PropertyCardMini";
import { Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useMemo, useRef } from "react";
import GetLinkButton from "./GetLinkButton";

export default function ShareModal() {
    const {
        isOpenShareModal,
        closeShareModal,
        propertySelected: property,
    } = useContext(GlobalContext);

    const modalRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                closeShareModal();
            }
        };
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                closeShareModal();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <Transition
            show={isOpenShareModal}
            as={Fragment}
            enter="transition-all ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-all ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="fixed top-0 left-0 w-full h-full bg-light/60 z-30 text-light flex flex-col justify-center">
                <Transition.Child
                    as={Fragment}
                    enter="transition-all ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition-all ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <div
                        ref={modalRef}
                        className="max-w-4xl mx-auto bg-white w-content max-h-screen py-4 px-4 sm:px-8 rounded-md"
                    >
                        <div className="relative sm:p-4">
                            <h2 className="text-xl sm:text-3xl text-start sm:text-center">
                                SHARE PROPERTY
                            </h2>
                            <button
                                onClick={() => {
                                    closeShareModal();
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="32"
                                    viewBox="0 -960 960 960"
                                    width="32"
                                    fill="currentColor"
                                >
                                    <path d="M256-227.69 227.69-256l224-224-224-224L256-732.31l224 224 224-224L732.31-704l-224 224 224 224L704-227.69l-224-224-224 224Z" />
                                </svg>
                            </button>
                        </div>
                        <div className="mx-auto w-content py-8">
                            <div className="max-w-2xl mx-auto flex justify-center gap-8 md:justify-between flex-wrap font-serif text-lg">
                                <a
                                    href={`https://api.whatsapp.com/send?text=${property?.url}`}
                                    className="flex items-center gap-2 uppercase hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                    target="_blank"
                                >
                                    <svg
                                        className="size-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
                                        />
                                    </svg>
                                    <span className="max-md:hidden">
                                        WhatsApp
                                    </span>
                                </a>
                                <a
                                    href={`mailto:?body=${property?.url}`}
                                    className="flex items-center gap-2 uppercase hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                    target="_blank"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="24"
                                        viewBox="0 -960 960 960"
                                        width="24"
                                        fill="currentColor"
                                    >
                                        <path d="M172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31ZM800-662.31 499.46-469.92q-4.61 2.61-9.54 4.11-4.92 1.5-9.92 1.5t-9.92-1.5q-4.93-1.5-9.54-4.11L160-662.31v410q0 5.39 3.46 8.85t8.85 3.46h615.38q5.39 0 8.85-3.46t3.46-8.85v-410ZM480-520l313.85-200h-627.7L480-520ZM160-662.31v9.23-45.73 1.19V-720v22.38-1.27V-653.08v-9.23V-240v-422.31Z" />
                                    </svg>
                                    <span className="max-md:hidden">Mail</span>
                                </a>
                                <a
                                    href={`https://www.linkedin.com/shareArticle?url=${property?.url}`}
                                    className="flex items-center gap-2 hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                    target="_blank"
                                >
                                    <svg
                                        className="size-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M100.3 448H7.4V148.9h92.9zM53.8 108.1C24.1 108.1 0 83.5 0 53.8a53.8 53.8 0 0 1 107.6 0c0 29.7-24.1 54.3-53.8 54.3zM447.9 448h-92.7V302.4c0-34.7-.7-79.2-48.3-79.2-48.3 0-55.7 37.7-55.7 76.7V448h-92.8V148.9h89.1v40.8h1.3c12.4-23.5 42.7-48.3 87.9-48.3 94 0 111.3 61.9 111.3 142.3V448z"
                                        />
                                    </svg>
                                    <span>LinkedIn</span>
                                </a>
                                {property && (
                                    <GetLinkButton url={property?.url} />
                                )}
                            </div>
                        </div>
                        {property && (
                            <div className="my-8 max-w-2xl mx-auto w-full">
                                <PropertyCardMini
                                    property={property}
                                    tab={"share"}
                                />
                            </div>
                        )}
                    </div>
                </Transition.Child>
            </div>
        </Transition>
    );
}
